/**
 * Global keyboard model (SPEC §6): Space = play/stop everywhere (default
 * prevented so a focused button does not also click), R = record, 1–4 = bar
 * tabs, M = mute the selected track, Cmd/Ctrl+Z = undo, ? = shortcuts
 * popover, Esc = close popovers / stop. Keys are ignored while typing in an
 * input, select or textarea, inside a dialog (the ask modal, the popover), and
 * when another handler (e.g. the audition card's 1/2/3) already consumed the
 * event — except Space on a grid cell, which the grid prevents only to stop
 * scrolling. `keydown` on the document is also an audio-unlock gesture — the
 * controller listens for that itself.
 *
 * Undo of an agent action goes through the ledger entry that owns it (its
 * undo closure), so the "Agent activity" panel shows it as undone too.
 */
import type { Store } from '../state/store-api';
import type { Controller, Engine } from '../app/controller';
import { BARS, type TrackId } from '../types';
import { ledger } from '../webmcp/ledger';
import { h, injectStyle } from './dom';

/** Dependencies for `installShortcuts()`. */
export interface ShortcutsDeps {
  store: Store;
  controller: Pick<Controller, 'togglePlay' | 'stop' | 'startRecording' | 'stopRecording' | 'isRecording'>;
  engine: Pick<Engine, 'isPlaying'>;
  /** Where the popover mounts (defaults to `document.body`). */
  container?: HTMLElement;
}

/** Installed shortcuts: popover control + dispose. */
export interface ShortcutsHandle {
  openHelp(): void;
  closeHelp(): void;
  isHelpOpen(): boolean;
  dispose(): void;
}

/** The list rendered in the "?" popover. */
export const SHORTCUTS: ReadonlyArray<readonly [keys: string, action: string]> = [
  ['Space', 'Play / stop'],
  ['R', 'Record (arm / stop)'],
  ['1 – 4', 'Select bar'],
  ['M', 'Mute the selected track'],
  ['⌘Z / Ctrl+Z', 'Undo'],
  ['↑ ↓ ← →', 'Move in the grid'],
  ['Enter / Shift+Enter', 'Toggle step / accent'],
  ['a w s e d f t g y h u j k o l p ;', 'Play the piano from C4'],
  ['Shift + key', 'Accent'],
  ['Esc', 'Close / stop'],
  ['?', 'This list'],
];

const CSS = `
.bm-shortcuts{position:fixed;right:1rem;bottom:1rem;z-index:960;width:min(22rem,calc(100vw - 2rem));background:var(--surface-1);color:var(--fg);
  border:1px solid var(--border-strong);border-radius:var(--radius-l);box-shadow:var(--shadow-m);padding:.9rem 1rem}
.bm-shortcuts__head{display:flex;align-items:center;justify-content:space-between;margin-bottom:.5rem}
.bm-shortcuts__head h2{margin:0;font-size:.95rem}
.bm-shortcuts dl{margin:0;display:grid;grid-template-columns:auto 1fr;gap:.3rem .8rem;font-size:.85rem}
.bm-shortcuts dt{color:var(--fg-muted);white-space:nowrap}
.bm-shortcuts dd{margin:0}
`;

function isTextField(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.matches !== 'function') return false;
  return el.matches('input, select, textarea, [contenteditable=""], [contenteditable="true"]') || !!el.closest?.('[contenteditable=""], [contenteditable="true"]');
}

/** True when `t` sits inside a dialog (the ask modal, the shortcuts popover): its own keys win there. */
function inDialog(t: EventTarget | null): HTMLElement | null {
  const el = t as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return null;
  return el.closest('[role="dialog"], [role="alertdialog"]');
}

/** True when `t` is a sequencer grid cell (the grid prevents Space itself to stop the page scrolling). */
function inGrid(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && typeof el.closest === 'function' && !!el.closest('[role="grid"]');
}

function ledgerUndoIdOf(result: unknown): string | null {
  if (result && typeof result === 'object' && typeof (result as { undo_id?: unknown }).undo_id === 'string') {
    return (result as { undo_id: string }).undo_id;
  }
  return null;
}

/** Undo one step: agent entries through their ledger closure, otherwise `store.undo(1)`. Returns the label undone, or null. */
export function undoOnce(store: Store): string | null {
  const top = store.undoStack()[0];
  if (!top) return null;
  if (top.by === 'agent') {
    const entry = ledger.entries().find((e) => !e.undone && !!e.undo && ledgerUndoIdOf(e.result) === top.id);
    if (entry) {
      // The closure is expected to revert the store itself (`store.undoTo(id)`); if it
      // settles without doing so, fall back to a plain pop. Waiting for it to settle
      // (rather than checking synchronously) keeps an async closure from undoing twice.
      const fallback = () => {
        if (store.undoStack()[0]?.id === top.id) store.undo(1);
      };
      ledger
        .undo(entry.id)
        .then(fallback, (err) => {
          console.error('[bandmate] ledger undo failed', err);
          fallback();
        });
      return top.label;
    }
  }
  const r = store.undo(1);
  return r.applied ? (r.undone[0]?.label ?? top.label) : null;
}

/** Install the global shortcut handler; returns a handle to open/close the popover and dispose. */
export function installShortcuts(deps: ShortcutsDeps): ShortcutsHandle {
  injectStyle('bm-shortcuts-css', CSS);
  const { store, controller, engine } = deps;
  const container = deps.container ?? document.body;
  let popover: HTMLElement | null = null;
  let previouslyFocused: HTMLElement | null = null;

  const closeHelp = () => {
    if (!popover) return;
    popover.remove();
    popover = null;
    previouslyFocused?.focus?.();
    previouslyFocused = null;
  };
  const openHelp = () => {
    if (popover) return;
    previouslyFocused = document.activeElement as HTMLElement | null;
    const close = h('button', { type: 'button', class: 'bm-btn bm-btn--small', 'aria-label': 'Close shortcuts' }, 'Close');
    close.addEventListener('click', closeHelp);
    popover = h(
      'div',
      { class: 'bm-shortcuts', role: 'dialog', 'aria-labelledby': 'bm-shortcuts-title', 'aria-modal': 'false' },
      h('div', { class: 'bm-shortcuts__head' }, h('h2', { id: 'bm-shortcuts-title' }, 'Keyboard shortcuts'), close),
      h('dl', null, SHORTCUTS.flatMap(([k, a]) => [h('dt', null, k), h('dd', null, a)])),
    );
    container.append(popover);
    close.focus();
  };

  const selectBar = (bar: number) => {
    if (store.get().ui.selectedBar !== bar) store.apply({ type: 'select', bar }, { by: 'human', label: `selected bar ${bar + 1}` });
    store.logHuman({ kind: 'select', detail: `selected bar ${bar + 1}` });
  };
  const toggleMute = () => {
    const id: TrackId = store.get().ui.selectedTrack;
    const muted = !store.get().tracks[id].muted;
    store.apply({ type: 'setTrack', track: id, muted }, { by: 'human', label: `${muted ? 'muted' : 'unmuted'} ${id}` });
    store.logHuman({ kind: muted ? 'mute' : 'unmute', detail: `${muted ? 'muted' : 'unmuted'} ${id}` });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (isTextField(e.target)) return;
    const isSpace = e.key === ' ' || e.key === 'Spacebar';
    // A handler closer to the target already consumed the key (ask dialog digits,
    // audition card 1/2/3/Esc) — except Space in the grid, which the grid prevents
    // only to stop the page from scrolling; play/stop must still happen.
    if (e.defaultPrevented && !(isSpace && inGrid(e.target))) return;
    // Esc always closes our popover; every other key inside a dialog belongs to it.
    const dialog = inDialog(e.target);
    if (dialog) {
      if (popover && dialog === popover && e.key === 'Escape') {
        e.preventDefault();
        closeHelp();
      }
      return;
    }
    const mod = e.metaKey || e.ctrlKey;
    if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      const label = undoOnce(store);
      if (label !== null) store.logHuman({ kind: 'undo', detail: `undid ${label}`.slice(0, 80) });
      return;
    }
    if (mod || e.altKey) return;
    switch (e.key) {
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        controller.togglePlay();
        return;
      case 'r':
      case 'R':
        e.preventDefault();
        if (controller.isRecording()) controller.stopRecording();
        else void controller.startRecording();
        return;
      case 'm':
      case 'M':
        e.preventDefault();
        toggleMute();
        return;
      case '?':
        e.preventDefault();
        if (popover) closeHelp();
        else openHelp();
        return;
      case 'Escape':
        if (popover) {
          e.preventDefault();
          closeHelp();
        } else if (controller.isRecording() || engine.isPlaying()) {
          e.preventDefault();
          controller.stop();
        }
        return;
      default: {
        const n = Number(e.key);
        if (Number.isInteger(n) && n >= 1 && n <= BARS && e.key.length === 1) {
          e.preventDefault();
          selectBar(n - 1);
        }
      }
    }
  };
  document.addEventListener('keydown', onKeyDown);

  return {
    openHelp,
    closeHelp,
    isHelpOpen: () => popover !== null,
    dispose() {
      document.removeEventListener('keydown', onKeyDown);
      closeHelp();
    },
  };
}
